import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserPlus, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import Varni2FlyLogo from '../components/Varni2FlyLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BRegister = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', agency_name: '', email: '', phone: '', password: '', confirm: '',
    gst_iata: '', city: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const { confirm, ...payload } = form;
      await axios.post(`${API}/b2b/register`, payload);
      setSuccess(true);
      toast.success('Registration submitted! Check your email.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D8DB4]/10 via-white to-[#F7945D]/10 p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-white" size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Registration Submitted</h2>
            <p className="text-gray-600 text-sm mb-4">
              Thanks for joining Varni Visa B2B partner network! Our team will review your application
              and approve your account within 24 hours. You&apos;ll receive an email at <b>{form.email}</b> once approved.
            </p>
            <Link to="/">
              <Button className="bg-[#2D8DB4] hover:bg-[#247a9a]">Back to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D8DB4]/10 via-white to-[#F7945D]/10 p-4">
      <SEO title="Register as B2B Travel Agent - Varni Visa Partner Network" path="/b2b/register" />
      <Card className="w-full max-w-2xl shadow-2xl my-8">
        <CardHeader className="text-center pb-3">
          <div className="flex justify-center mb-3">
            <Varni2FlyLogo size="sm" />
          </div>
          <CardTitle className="text-2xl flex items-center justify-center gap-2">
            <UserPlus size={22} className="text-[#F7945D]" /> Become a B2B Partner
          </CardTitle>
          <CardDescription>Get net rates, white-label itineraries & ground handling support</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Contact Person *</Label>
                <Input id="name" data-testid="b2b-reg-name" required value={form.name} onChange={handle('name')} />
              </div>
              <div>
                <Label htmlFor="agency">Agency Name *</Label>
                <Input id="agency" data-testid="b2b-reg-agency" required value={form.agency_name} onChange={handle('agency_name')} />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" data-testid="b2b-reg-email" type="email" required value={form.email} onChange={handle('email')} />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" data-testid="b2b-reg-phone" required value={form.phone} onChange={handle('phone')} />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" data-testid="b2b-reg-city" value={form.city} onChange={handle('city')} />
              </div>
              <div>
                <Label htmlFor="gst">GST / IATA No.</Label>
                <Input id="gst" data-testid="b2b-reg-gst" value={form.gst_iata} onChange={handle('gst_iata')} />
              </div>
              <div>
                <Label htmlFor="password">Password * (min 6 chars)</Label>
                <Input id="password" data-testid="b2b-reg-password" type="password" required value={form.password} onChange={handle('password')} />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm Password *</Label>
                <Input id="confirm" type="password" required value={form.confirm} onChange={handle('confirm')} />
              </div>
            </div>
            <Button type="submit" data-testid="b2b-reg-submit" className="w-full bg-[#F7945D] hover:bg-[#e67f3d] h-11" disabled={loading}>
              {loading ? 'Submitting...' : 'Register for B2B Access'}
            </Button>
            <p className="text-xs text-center text-gray-500">
              Already a partner? <Link to="/b2b/login" className="text-[#2D8DB4] font-semibold hover:underline">Sign in</Link>
            </p>
            <p className="text-xs text-center text-gray-400">
              <Link to="/" className="hover:underline">← Back to varnivisa.com</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default B2BRegister;
